import { useState } from "react";
import CustomSelect from "../../../DesignSystem/Dropdowns/CustomSelect";
import NewDateField from "../../../DesignSystem/Inputs/NewDateField";
import NewInputField from "../../../DesignSystem/Inputs/NewInputField";
import NewTextArea from "../../../DesignSystem/Inputs/NewTextArea";
import RadioGroup from "../../../DesignSystem/Inputs/RadioGroup";
import PrimaryButton from "../../../DesignSystem/Buttons/PrimaryButton";
import SecondaryButton from "../../../DesignSystem/Buttons/SecondaryButton";
import { createManualPayment, deleteManualPayment, updateManualPayment } from "../services/manualPaymentService";
import { ReactComponent as CloseIcon } from "../../../../assets/icons/close.svg";

const PAYMENT_MODE_OPTIONS = [
  { value: "one_time", label: "One-Time Payment" },
  { value: "recurring", label: "Recurring Payment" },
  { value: "advance", label: "Advances" },
];

const AddPayment = ({
  adviser,
  payment,
  initialType = "additional_payment",
  onClose,
  asPage = false,
  defaultDate,
}) => {
  const [paymentMode, setPaymentMode] = useState(
    payment?.paymentType === "advance" || initialType === "advance"
      ? "advance"
      : payment?.billingType === "recurring"
        ? "recurring"
        : "one_time",
  );
  const paymentType = paymentMode === "advance" ? "advance" : "additional_payment";
  const billingType = paymentMode === "recurring" ? "recurring" : "one_time";
  const [form, setForm] = useState({
    date: String(payment?.date || defaultDate || new Date().toISOString()).slice(0, 10),
    description: payment?.description || "",
    amount: payment?.amountPaid == null
      ? ""
      : String(
          (payment.paymentType || initialType) === "additional_payment"
            ? Math.abs(Number(payment.amountPaid || 0))
            : payment.amountPaid,
        ),
    notes: payment?.notes || "",
    paymentTo: String(payment?.paymentTo || "").slice(0, 10),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const update = (key) => (event) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));
  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const values = {
        ...form,
        paymentType,
        billingType,
        adviserEmail: adviser.email,
        adviserName: adviser.name,
        adviserId: adviser.id,
        brandName: adviser.brand,
      };
      if (payment?.id) await updateManualPayment({ paymentId: payment.id, ...values });
      else await createManualPayment(values);
      onClose(paymentType);
    } catch (saveError) {
      setError(saveError.message || "The payment could not be saved.");
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    if (!payment?.id) return onClose(paymentType);
    setSaving(true);
    try {
      await deleteManualPayment(payment.id);
      onClose(paymentType);
    } catch (deleteError) {
      setError(deleteError.message || "The payment could not be removed.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <form
      className={`ParaplanningModal ${asPage ? "add-charge-page-card" : "add-payment-modal"}`}
      role={asPage ? undefined : "dialog"}
      aria-modal={asPage ? undefined : "true"}
      aria-label={payment?.id ? "Edit payment" : "Add payment"}
      onSubmit={submit}
      onMouseDown={(event) => event.stopPropagation()}
    >
      {!asPage ? (
        <div className="ParaplanningModal__Header">
          <h2 className="FP_H3">{payment?.id ? "Edit Payment" : "Add Payment"}</h2>
          <button type="button" aria-label="Close" onClick={() => onClose(paymentType)}>
            <CloseIcon />
          </button>
        </div>
      ) : null}
      <div className="ParaplanningModal__Grid">
        <CustomSelect
          label="Adviser Name"
          value={adviser.email}
          selectedLabel={adviser.name}
          options={[{ id: adviser.email, name: adviser.name }]}
          disabled
          searchable={false}
          clearable={false}
        />
        {billingType !== "recurring" ? (
          <NewDateField
            label="Date"
            value={form.date}
            onChange={update("date")}
            required
          />
        ) : null}
      </div>
      <RadioGroup
        name="manual-payment-mode"
        options={PAYMENT_MODE_OPTIONS}
        value={paymentMode}
        onChange={(event) => setPaymentMode(event.target.value)}
        inline
      />
      {billingType === "recurring" ? (
        <div className="ParaplanningModal__Grid">
          <NewDateField
            label="Payment From"
            value={form.date}
            onChange={update("date")}
            required
          />
          <NewDateField
            label="Payment To"
            value={form.paymentTo}
            onChange={update("paymentTo")}
            required
          />
        </div>
      ) : null}
      <NewTextArea
        id="payment-description"
        label="Payment Description"
        value={form.description}
        onChange={update("description")}
        placeholder="Details/reason of payment"
      />
      <NewInputField
        id="payment-amount"
        label="Total Amount (£)"
        type="number"
        value={form.amount}
        onChange={update("amount")}
        placeholder="Amount to pay"
        required
      />
      <NewTextArea
        id="payment-notes"
        label="Additional Notes"
        value={form.notes}
        onChange={update("notes")}
        placeholder="Additional notes"
      />
      {error ? <p className="FP_Content text-error">{error}</p> : null}
      <div className="ParaplanningModal__Actions">
        <SecondaryButton
          type="button"
          label="Remove Payment"
          onClick={remove}
          disabled={saving}
        />
        <PrimaryButton
          type="submit"
          label={saving ? "Saving…" : payment?.id ? "Save Changes" : "Add Payment"}
          disabled={saving}
        />
      </div>
    </form>
  );
};

export default AddPayment;
